<template>
  <div :class="$style.wrapper">
    <label v-if="label" :class="$style.label">{{ label }}</label>
    <div :class="$style.inputsWrapper">
      <input
        ref="verificationCodeInput"
        v-model="verificationCode"
        type="text"
        inputmode="numeric"
        name="verification-code"
        pattern="[0-9]"
        :maxlength="codeLength"
        autocomplete="one-time-code"
        :class="$style.input"
        @input="handleInput()"
        @keydown="validateDigit"
      />
    </div>
    <div v-if="!!$slots.errorMessages" :class="[$style.message, $style.errorMessages]">
      <slot name="errorMessages" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useSlots } from 'vue';

interface Props {
  label?: string;
  code?: string | null;
  codeLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  code: null,
  codeLength: 5,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const slots = useSlots();

const verificationCodeInput = ref<HTMLInputElement>();
const verificationCode = ref(props.code);

onMounted(() => {
  if (!props.code?.length) {
    verificationCodeInput.value?.focus();
  }
});

const handleInput = () => {
  emit('update:modelValue', verificationCode.value || '');
};

const validateDigit = (event: KeyboardEvent) => {
  const key = event.key;
  if (isNaN(parseInt(key)) && key !== 'Backspace' && key !== 'Delete') {
    event.preventDefault();
  }
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.wrapper {
  position: relative;
  box-sizing: border-box;
  display: block;

  * {
    box-sizing: border-box;
  }
}

.input {
  @include input;

  & {
    text-align: center;
    letter-spacing: 5px;
  }
}

.label {
  cursor: auto;

  @include label;
}

.message {
  margin-top: $space-25;

  @include body-3;

  > * {
    display: block;
    margin: 0 0 $space-25 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.errorMessages {
  color: var(--validation-error-color);
}
</style>
